import React, { useState, useEffect, useRef } from 'react';
import Card from '../../../Shared/Components/Card/Card';

const ForestSounds = ({ updateStats }) => {
    const [activeEnvironment, setActiveEnvironment] = useState(null);
    const [volume, setVolume] = useState(70);
    const [sessionTime, setSessionTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const audioContextRef = useRef(null);
    const gainNodeRef = useRef(null);
    const oscillatorsRef = useRef([]);

    const environments = [
        {
            id: 'rainforest',
            name: 'Tropical Rainforest',
            description: 'Gentle rain and exotic birds',
            emoji: '🌴',
            gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            sounds: ['🦜 Birds', '🌧️ Rain', '🐸 Frogs', '🦗 Insects'],
            noiseType: 'Brown Noise'
        },
        {
            id: 'pine',
            name: 'Pine Forest',
            description: 'Wind through the trees',
            emoji: '🌲',
            gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            sounds: ['🌬️ Wind', '🦉 Owl', '🌲 Rustling', '🦌 Wildlife'],
            noiseType: 'Filtered White Noise'
        },
        {
            id: 'beach',
            name: 'Ocean Beach',
            description: 'Waves and seagulls',
            emoji: '🏖️',
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            sounds: ['🌊 Waves', '🦅 Seagulls', '🌬️ Breeze', '🐚 Shells'],
            noiseType: 'Modulated Brown Noise'
        },
        {
            id: 'mountain',
            name: 'Mountain Stream',
            description: 'Flowing water and alpine birds',
            emoji: '⛰️',
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            sounds: ['💧 Stream', '🦅 Eagles', '🌊 Waterfall', '🌬️ Wind'],
            noiseType: 'Pink Noise'
        }
    ];

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            stopAudio();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Timer for session tracking
    useEffect(() => {
        let interval;
        if (isPlaying) {
            interval = setInterval(() => {
                setSessionTime(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isPlaying]);

    // Update volume when slider changes
    useEffect(() => {
        if (gainNodeRef.current) {
            gainNodeRef.current.gain.value = volume / 100;
        }
    }, [volume]);

    const createAmbientSound = (context, type) => {
        // Create different ambient sounds based on environment type
        const oscillators = [];

        if (type === 'rainforest') {
            // Brown noise for rain (low frequency rumble)
            const bufferSize = 2 * context.sampleRate;
            const noiseBuffer = context.createBuffer(1, bufferSize, context.sampleRate);
            const output = noiseBuffer.getChannelData(0);
            let lastOut = 0.0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                output[i] = (lastOut + (0.02 * white)) / 1.02;
                lastOut = output[i];
                output[i] *= 3.5;
            }
            const whiteNoise = context.createBufferSource();
            whiteNoise.buffer = noiseBuffer;
            whiteNoise.loop = true;

            const filter = context.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.value = 800;
            whiteNoise.connect(filter);

            oscillators.push({ node: whiteNoise, output: filter });

            // Add some bird-like chirps (intermittent sine waves)
            const birdOsc = context.createOscillator();
            birdOsc.type = 'sine';
            birdOsc.frequency.setValueAtTime(1200, context.currentTime);

            const birdGain = context.createGain();
            birdGain.gain.value = 0.05;
            birdOsc.connect(birdGain);

            oscillators.push({ node: birdOsc, output: birdGain });

        } else if (type === 'pine') {
            // Wind sound (filtered white noise with slow modulation)
            const bufferSize = 2 * context.sampleRate;
            const noiseBuffer = context.createBuffer(1, bufferSize, context.sampleRate);
            const output = noiseBuffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) {
                output[i] = Math.random() * 2 - 1;
            }
            const whiteNoise = context.createBufferSource();
            whiteNoise.buffer = noiseBuffer;
            whiteNoise.loop = true;

            const filter = context.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.value = 500;
            filter.Q.value = 0.5;
            whiteNoise.connect(filter);

            const windGain = context.createGain();
            windGain.gain.value = 0.3;
            filter.connect(windGain);

            oscillators.push({ node: whiteNoise, output: windGain });

        } else if (type === 'beach') {
            // Ocean waves (low frequency oscillation with noise)
            const bufferSize = 4 * context.sampleRate;
            const noiseBuffer = context.createBuffer(1, bufferSize, context.sampleRate);
            const output = noiseBuffer.getChannelData(0);
            let lastOut = 0.0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                output[i] = (lastOut + (0.02 * white)) / 1.02;
                lastOut = output[i];
                output[i] *= 3.5;
            }
            const noise = context.createBufferSource();
            noise.buffer = noiseBuffer;
            noise.loop = true;

            const filter = context.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.value = 400;
            noise.connect(filter);

            // Wave modulation
            const waveGain = context.createGain();
            waveGain.gain.value = 0.3;
            filter.connect(waveGain);

            const lfo = context.createOscillator();
            lfo.type = 'sine';
            lfo.frequency.value = 0.2; // Slow wave rhythm
            const lfoGain = context.createGain();
            lfoGain.gain.value = 0.2;
            lfo.connect(lfoGain);
            lfoGain.connect(waveGain.gain);

            oscillators.push({ node: noise, output: waveGain });
            oscillators.push({ node: lfo, output: null });

        } else if (type === 'mountain') {
            // Stream/waterfall sound (pink noise)
            const bufferSize = 2 * context.sampleRate;
            const noiseBuffer = context.createBuffer(1, bufferSize, context.sampleRate);
            const output = noiseBuffer.getChannelData(0);
            let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                b0 = 0.99886 * b0 + white * 0.0555179;
                b1 = 0.99332 * b1 + white * 0.0750759;
                b2 = 0.96900 * b2 + white * 0.153852;
                b3 = 0.86650 * b3 + white * 0.3104856;
                b4 = 0.55000 * b4 + white * 0.5329522;
                b5 = -0.7616 * b5 - white * 0.0168980;
                output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
                output[i] *= 0.11;
                b6 = white * 0.115926;
            }
            const pinkNoise = context.createBufferSource();
            pinkNoise.buffer = noiseBuffer;
            pinkNoise.loop = true;

            const streamGain = context.createGain();
            streamGain.gain.value = 0.4;
            pinkNoise.connect(streamGain);

            oscillators.push({ node: pinkNoise, output: streamGain });
        }

        return oscillators;
    };

    const startAudio = (envType) => {
        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        // Create master gain node for volume control
        const gainNode = audioContext.createGain();
        gainNode.gain.value = volume / 100;
        gainNode.connect(audioContext.destination);
        gainNodeRef.current = gainNode;

        // Create and start ambient sounds
        const oscillators = createAmbientSound(audioContext, envType);
        oscillatorsRef.current = oscillators;

        oscillators.forEach(osc => {
            if (osc.output) {
                osc.output.connect(gainNode);
            }
            osc.node.start();
        });
    };

    const stopAudio = () => {
        // Stop all oscillators
        oscillatorsRef.current.forEach(osc => {
            try {
                osc.node.stop();
            } catch (e) {
                // Already stopped
            }
        });
        oscillatorsRef.current = [];

        // Close audio context
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        gainNodeRef.current = null;
    };

    const startEnvironment = (env) => {
        setActiveEnvironment(env);
        setIsPlaying(true);
        setSessionTime(0);
        startAudio(env.id);
    };

    const stopEnvironment = () => {
        if (sessionTime > 0) {
            updateStats('gamesPlayed', 1);
        }
        setIsPlaying(false);
        setActiveEnvironment(null);
        setSessionTime(0);
        stopAudio();
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (activeEnvironment) {
        return (
            <Card>
                <div style={{ padding: '32px' }}>
                    {/* Header */}
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <div style={{ fontSize: '4rem', marginBottom: '16px' }}>
                            {activeEnvironment.emoji}
                        </div>
                        <h4 style={{ color: '#1f2937', marginBottom: '8px' }}>
                            {activeEnvironment.name}
                        </h4>
                        <p style={{ color: '#6b7280', margin: 0 }}>
                            {activeEnvironment.description}
                        </p>
                    </div>

                    {/* Session Timer */}
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '32px',
                        padding: '24px',
                        background: `${activeEnvironment.gradient}`,
                        borderRadius: '16px',
                        color: 'white'
                    }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', opacity: 0.9, marginBottom: '8px' }}>
                            SESSION TIME
                        </div>
                        <div style={{ fontSize: '3rem', fontWeight: '700', textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                            {formatTime(sessionTime)}
                        </div>
                    </div>

                    {/* Animated Soundwaves */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '16px',
                        height: '100px'
                    }}>
                        {[...Array(12)].map((_, i) => (
                            <div
                                key={i}
                                style={{
                                    width: '8px',
                                    background: activeEnvironment.gradient,
                                    borderRadius: '4px',
                                    animation: `wave ${0.8 + (i * 0.1)}s ease-in-out infinite`,
                                    animationDelay: `${i * 0.1}s`,
                                    height: '20px'
                                }}
                            ></div>
                        ))}
                    </div>

                    {/* Playing Indicator */}
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '32px',
                        padding: '12px',
                        background: 'rgba(67, 231, 123, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(67, 231, 123, 0.3)'
                    }}>
                        <span style={{ color: '#10b981', fontSize: '0.9rem', fontWeight: '600' }}>
                            🔊 Audio Playing
                        </span>
                    </div>

                    <style>{`
                        @keyframes wave {
                            0%, 100% {
                                height: 20px;
                                opacity: 0.5;
                            }
                            50% {
                                height: 80px;
                                opacity: 1;
                            }
                        }
                    `}</style>

                    {/* Sound Layers */}
                    <div style={{
                        marginBottom: '32px',
                        padding: '24px',
                        background: '#f9fafb',
                        borderRadius: '12px'
                    }}>
                        <h6 style={{
                            color: '#1f2937',
                            marginBottom: '16px',
                            fontSize: '0.95rem',
                            fontWeight: '600'
                        }}>
                            Active Sound Layers
                        </h6>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: '12px'
                        }}>
                            {activeEnvironment.sounds.map((sound, index) => (
                                <div
                                    key={index}
                                    style={{
                                        padding: '12px',
                                        background: 'white',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        border: '2px solid #e5e7eb',
                                        fontSize: '0.9rem',
                                        fontWeight: '500',
                                        color: '#6b7280'
                                    }}
                                >
                                    {sound}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Volume Control */}
                    <div style={{
                        marginBottom: '32px',
                        padding: '24px',
                        background: '#f9fafb',
                        borderRadius: '12px'
                    }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '12px',
                            color: '#1f2937',
                            fontWeight: '600',
                            fontSize: '0.95rem'
                        }}>
                            Volume: {volume}%
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={volume}
                            onChange={(e) => setVolume(e.target.value)}
                            style={{
                                width: '100%',
                                height: '8px',
                                borderRadius: '4px',
                                outline: 'none',
                                cursor: 'pointer'
                            }}
                        />
                    </div>

                    {/* Control Button */}
                    <div style={{ textAlign: 'center' }}>
                        <button
                            onClick={stopEnvironment}
                            style={{
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '14px 48px',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(240, 147, 251, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(240, 147, 251, 0.3)';
                            }}
                        >
                            Stop Session
                        </button>
                    </div>

                    {/* Breathing Reminder */}
                    <div style={{
                        marginTop: '32px',
                        padding: '20px',
                        background: '#f9fafb',
                        borderRadius: '12px',
                        borderLeft: '4px solid #667eea',
                        textAlign: 'center'
                    }}>
                        <p style={{ color: '#6b7280', margin: 0, fontSize: '0.9rem' }}>
                            💆 Close your eyes and focus on your breathing. Let the sounds of nature wash over you.
                        </p>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <div style={{ padding: '32px' }}>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <h4 style={{ color: '#1f2937', marginBottom: '8px' }}>Forest Sounds</h4>
                    <p style={{ color: '#6b7280', margin: 0 }}>
                        Choose a natural environment to immerse yourself in calming sounds
                    </p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px'
                }}>
                    {environments.map(env => (
                        <div
                            key={env.id}
                            onClick={() => startEnvironment(env)}
                            style={{
                                background: env.gradient,
                                borderRadius: '16px',
                                padding: '32px',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 12px 28px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            {/* Decorative background */}
                            <div style={{
                                position: 'absolute',
                                top: '-40px',
                                right: '-40px',
                                width: '120px',
                                height: '120px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '50%'
                            }}></div>

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>
                                    {env.emoji}
                                </div>
                                <h5 style={{
                                    marginBottom: '8px',
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}>
                                    {env.name}
                                </h5>
                                <p style={{
                                    fontSize: '0.9rem',
                                    opacity: 0.9,
                                    marginBottom: '8px'
                                }}>
                                    {env.description}
                                </p>
                                <div style={{
                                    fontSize: '0.75rem',
                                    opacity: 0.85,
                                    marginBottom: '16px',
                                    fontStyle: 'italic'
                                }}>
                                    🔊 {env.noiseType}
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '6px',
                                    fontSize: '0.75rem'
                                }}>
                                    {env.sounds.map((sound, i) => (
                                        <span
                                            key={i}
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.2)',
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                backdropFilter: 'blur(10px)'
                                            }}
                                        >
                                            {sound}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Benefits */}
                <div style={{
                    marginTop: '32px',
                    padding: '24px',
                    background: '#f9fafb',
                    borderRadius: '12px',
                    borderLeft: '4px solid #43e97b'
                }}>
                    <h6 style={{ color: '#1f2937', marginBottom: '12px', fontSize: '0.95rem', fontWeight: '600' }}>
                        🌿 Benefits of Nature Sounds
                    </h6>
                    <ul style={{ color: '#6b7280', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                        <li>Reduces stress and anxiety levels</li>
                        <li>Improves focus and concentration</li>
                        <li>Promotes better sleep quality</li>
                        <li>Enhances mood and mental clarity</li>
                        <li>Connects you with environmental mindfulness</li>
                    </ul>
                </div>

                {/* Audio Note */}
                <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: '#fef3c7',
                    borderRadius: '8px',
                    borderLeft: '4px solid #f59e0b',
                    textAlign: 'center'
                }}>
                    <p style={{ color: '#92400e', margin: 0, fontSize: '0.85rem' }}>
                        🔊 <strong>Note:</strong> Audio is generated using Web Audio API. Make sure your browser volume is turned up!
                    </p>
                </div>
            </div>
        </Card>
    );
};

export default ForestSounds;
